import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Collapsible,
  FieldGroup,
  Money,
  UserSearchSelect,
} from "../../components";
import { DateInput } from "../../components/inputs";
import { Button } from "../../components/ui";
import { useEntitlement } from "../../contexts";
import DataHelper from "../../helpers/Data";
import { STEPS } from "../constants";
import { PaymentType, CardType } from "../atoms";
import { MANUAL_PAYMENT_METHODS } from "../paymentMethods";
import { ToggleButton } from "../../listings/atoms";
import { SectionTrigger } from "./SectionTrigger";
import { useOrderManagement } from "../useOrderManagement";
import { baseSymbol, symbolFor, currencyOptions, getBaseCurrency } from "../../core/currency";
import { useFx } from "../../hooks/fx";
import FxUnavailable from "../../components/formatters/FxUnavailable";

import "./commissions.scss";

// Payouts recorded against a Xero account: transfer, plus cash and USDT,
// which settle out of a Bank-type "Cash" / "USDT wallet" account in Xero.
const BANK_ACCOUNT_METHODS = ["offline", "cash", "usdt"];

const MAX_SLOTS = 3;

// Backend handles `store_admin` as a special branch in getUsersByType
// (returns is_sales_admin=1 admins) so we can use the standard route.
const optionsUrlFor = (userType) => `/api/users/by-type/${userType}`;

// Small lazy hook for the Xero bank-account list. Only fires when the
// rep flips a slot's Add-to-Cost ON with method=offline, so we don't
// hammer the endpoint on every page load.
const useBankAccounts = (enabled, connectionId) => {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    axios
      .get(`/api/xero/bank-accounts`, {
        params: connectionId ? { xero_connection_id: connectionId } : {},
      })
      .then(({ data }) => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : data?.items ?? [];
        setAccounts(
          items.map((a) => ({ id: a.id ?? a.account_id, name: a.name ?? a.bank_name }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, connectionId]);
  return accounts;
};

function CommissionSlot({
  itemId,
  slot,
  slotIdx,
  slotsCount,
  itemProfitPence,
  updateCommissionField,
  removeCommissionSlot,
}) {
  const pct = Number(slot.percentage) || 0;
  // itemProfitPence is the GBP per-item profit (om computes itemProfit in
  // GBP), so the raw share is GBP too. The rep picks a payout currency per
  // slot — convert the GBP share INTO that currency so the displayed amount
  // AND its symbol agree (previously a GBP value was shown under the slot's
  // chosen-currency symbol). useFx('gbp','gbp') / unseeded ⇒ rate 1 ⇒
  // byte-identical for a GBP slot.
  //
  // FX RULE (hooks/fx.js): gbpToSlot is null when no rate is known, and the
  // profit itself may be unconvertible (om.fxMissing). The amount is then
  // shown as "Rate unavailable", never as a 1:1 figure.
  const { rate: gbpToSlot, unavailable: gbpToSlotUnavailable } = useFx("gbp", slot.currency);
  const slotAmountGbpPence = (pct / 100) * itemProfitPence;
  const slotAmountPence = slotAmountGbpPence * (gbpToSlot || 1);
  const slotAmountPounds = slotAmountPence / 100;

  // Commission payouts post into the order's selected Xero org — load that org's
  // bank accounts so the rep can't pick one that doesn't exist there. A
  // natively invoiced order has no Xero org, so there is nothing to pick.
  const { xeroConnectionId, fxMissing, fxUnavailable, nativeInvoice } = useOrderManagement();
  const needsBankList =
    !nativeInvoice &&
    !!slot.add_to_cost && (BANK_ACCOUNT_METHODS.includes(slot.method) || !slot.method);
  const slotAmountHidden = fxMissing || gbpToSlot == null;
  const bankAccounts = useBankAccounts(needsBankList, xeroConnectionId);

  return (
    <div className="commission-slot">
      <div className="commission-slot__row commission-slot__row--main">
        <div className="commission-slot__cell commission-slot__cell--type">
          <FieldGroup
            placeholder="Type"
            type="select"
            options={DataHelper.userType}
            name="type"
            value={slot.user_type}
            onChange={(value) =>
              updateCommissionField(itemId, slotIdx, "user_type", value)
            }
            noFormik
          />
        </div>
        <div className="commission-slot__cell commission-slot__cell--user">
          {slot.user_type && (
            <UserSearchSelect
              placeholder="Search User"
              defaultInputValue={slot.user?.name}
              value={slot.user}
              onChange={(value) =>
                updateCommissionField(itemId, slotIdx, "user", value)
              }
              optionsUrl={optionsUrlFor(slot.user_type)}
              defaultOptions={slot.user ? [slot.user] : undefined}
            />
          )}
        </div>
        <div className="commission-slot__cell commission-slot__cell--pct">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="%"
            className="commission-slot__pct-input"
            value={slot.percentage ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              updateCommissionField(
                itemId,
                slotIdx,
                "percentage",
                raw === "" ? 0 : Number(raw)
              );
            }}
          />
          <span className="commission-slot__pct-suffix">%</span>
        </div>
        <div className="commission-slot__cell commission-slot__cell--toggle">
          <ToggleButton
            label="Add to Cost"
            checked={!!slot.add_to_cost}
            onChange={(value) =>
              updateCommissionField(itemId, slotIdx, "add_to_cost", value)
            }
          />
        </div>
        <div className="commission-slot__cell commission-slot__cell--remove">
          {slotsCount > 1 && (
            <Button
              variant="danger"
              size="sm"
              data-btn="commission-delete"
              onClick={() => removeCommissionSlot(itemId, slotIdx)}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      {/* Currency + auto-amount: visible regardless of Add-to-Cost. */}
      <div className="commission-slot__row commission-slot__row--amount">
        <div className="commission-slot__cell commission-slot__cell--currency">
          <FieldGroup
            name="currency"
            type="select"
            options={currencyOptions()}
            placeholder=""
            value={slot.currency}
            onChange={(value) =>
              updateCommissionField(itemId, slotIdx, "currency", value)
            }
            noFormik
          />
        </div>
        <div className="commission-slot__cell commission-slot__cell--amount-readonly">
          <span className="commission-slot__amount-label">Amount:</span>
          <span className="commission-slot__amount-value">
            {slotAmountHidden ? (
              <FxUnavailable unavailable={fxUnavailable || gbpToSlotUnavailable} />
            ) : (
              <Money
                amount={slotAmountPence}
                currency={slot.currency}
                zero={<span>{symbolFor(slot.currency ?? "gbp")}0.00</span>}
              />
            )}
          </span>
        </div>
      </div>

      {/* Payment recording fields — only when Add-to-Cost is ON. */}
      {slot.add_to_cost && (
        <div className="commission-slot__row commission-slot__row--payment">
          <div className="commission-slot__cell commission-slot__cell--method">
            <PaymentType
              value={slot.method}
              onChange={(value) =>
                updateCommissionField(itemId, slotIdx, "method", value)
              }
              allowedMethods={MANUAL_PAYMENT_METHODS}
            />
          </div>
          {slot.method === "card" && (
            <div className="commission-slot__cell commission-slot__cell--card-type">
              <CardType
                value={slot.card_type}
                onChange={(value) =>
                  updateCommissionField(itemId, slotIdx, "card_type", value)
                }
              />
            </div>
          )}
          <div className="commission-slot__cell commission-slot__cell--date">
            <DateInput
              name={`commission-date-${itemId}-${slotIdx}`}
              value={slot.date}
              onChange={(value) =>
                updateCommissionField(itemId, slotIdx, "date", value)
              }
              maxAllowedDate={new Date()}
              popperClassName="rc-dark-calendar"
              autocomplete="off"
              dateFormat="yyyy-MM-dd"
            />
          </div>
          <div className="commission-slot__cell commission-slot__cell--fee">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="Fee"
              className="commission-slot__fee-input"
              value={slot.fee ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                updateCommissionField(
                  itemId,
                  slotIdx,
                  "fee",
                  raw === "" ? 0 : Number(raw)
                );
              }}
            />
          </div>
          {BANK_ACCOUNT_METHODS.includes(slot.method) && !nativeInvoice && (
            <div className="commission-slot__cell commission-slot__cell--bank">
              <FieldGroup
                name={`bank-${itemId}-${slotIdx}`}
                type="select"
                options={bankAccounts}
                placeholder="Bank account"
                value={slot.bank_account_id}
                onChange={(value) =>
                  updateCommissionField(
                    itemId,
                    slotIdx,
                    "bank_account_id",
                    value
                  )
                }
                noFormik
              />
            </div>
          )}
        </div>
      )}

      {/* Suppress the unused slotAmountPounds lint while keeping the
          conversion close to where it's consumed if the design later
          asks for an editable amount field. */}
      <span style={{ display: "none" }}>{slotAmountPounds}</span>
    </div>
  );
}

export function Commissions({
  step,
  lastStep,
  commissions,
  updateCommissionField,
  addCommissionSlot,
  removeCommissionSlot,
  itemProfit,
  saveCommissions,
  items,
  netProfitBeforeCommissions,
  commissionsAmount,
  isPxOrder,
}) {
  const {
    isInline,
    setStep,
    // GBP→base rate for the GBP-pivot profit/commission totals below. A
    // listing's own price is labelled from the listing's own record, not from
    // the deal currency — see the heading below.
    gbpToBaseRate,
    // FX RULE (hooks/fx.js): with no known rate the totals below are hidden
    // ("Rate unavailable"), never shown converted 1:1.
    fxMissing,
    fxUnavailable,
  } = useOrderManagement();
  const hasCommissions = useEntitlement("commissions_split");
  const baseCcy = getBaseCurrency();
  // gbpToBaseRate is exactly 1 for a GBP base; null = GBP -> base unknown.
  const { unavailable: gbpToBaseUnavailable } = useFx("gbp", baseCcy);
  const toBase = gbpToBaseRate || 1;
  const fxHidden = fxMissing || gbpToBaseRate == null;
  // Commissions split (Feature 42): the whole section disappears from the
  // order / PX-order flow without the plan. Commissions already recorded are
  // untouched and keep counting toward profit.
  if (!hasCommissions) return null;
  if (lastStep < STEPS.COMMISSIONS) return null;

  const netProfitAfterCommissions =
    (netProfitBeforeCommissions ?? 0) - (commissionsAmount ?? 0);

  return (
    <Collapsible
      trigger={<SectionTrigger icon="commissions" label="Commissions" />}
      open={step === STEPS.COMMISSIONS}
      handleTriggerClick={
        isInline
          ? () => setStep(step === STEPS.COMMISSIONS ? -1 : STEPS.COMMISSIONS)
          : undefined
      }
    >
      <div className="order-commissions mt-4">
        {items.map((item, idx) => {
          const slots = commissions[item.id]?.slots ?? [];
          const profit = itemProfit ? itemProfit(item) : 0;
          const pctSum = slots.reduce(
            (s, slot) => s + (Number(slot.percentage) || 0),
            0
          );
          const sumValid = Math.abs(pctSum - 100) < 0.01;

          return (
            <div key={item.id} className="commission-item">
              <div className="title mt-5 mb-3">
                Listing {idx + 1} |&nbsp;
                {item.listing?.product?.brand?.name}{" "}
                {item.listing?.product?.model?.name} |&nbsp;
                {item.listing?.product?.reference_number} |&nbsp;
                {/* The LISTING's own price in the LISTING's own currency — the
                    same pairing the Payments listing heading and the Order
                    Summary cart line use, so all three can never disagree about
                    one listing's price. Labelling it with the order/deal currency
                    paired one record's amount with another record's currency, and
                    on a multi-listing order priced per line it mislabelled every
                    line after the first. NULL currency = legacy GBP. */}
                <Money
                  amount={item.listing?.price}
                  currency={item.listing?.currency || "gbp"}
                  className="title"
                />
              </div>

              {slots.map((slot, slotIdx) => (
                <CommissionSlot
                  key={slot.key}
                  itemId={item.id}
                  slot={slot}
                  slotIdx={slotIdx}
                  slotsCount={slots.length}
                  itemProfitPence={profit}
                  updateCommissionField={updateCommissionField}
                  removeCommissionSlot={removeCommissionSlot}
                />
              ))}

              <div className="commission-item__footer">
                {slots.length < MAX_SLOTS && (
                  <Button
                    variant="secondary"
                    size="sm"
                    data-btn="commission-add"
                    onClick={() => addCommissionSlot(item.id)}
                  >
                    + Add commission
                  </Button>
                )}
                <div
                  className={`commission-item__sum ${
                    sumValid ? "" : "commission-item__sum--error"
                  }`}
                >
                  Split total: <strong>{pctSum.toFixed(2)}%</strong>
                  {!sumValid && <> — must equal 100%</>}
                </div>
              </div>
            </div>
          );
        })}

        <div className="commission-totals">
          <div className="commission-totals__line">
            <span className="commission-totals__pair">
              <span className="strong-label">
                Net Profit Before Commissions:
              </span>
              <span className="value">
                {/* GBP pivot → store base for display (×1 for GBP base). */}
                {fxHidden ? (
                  <FxUnavailable unavailable={fxUnavailable || gbpToBaseUnavailable} />
                ) : (
                  <Money
                    amount={netProfitBeforeCommissions * toBase}
                    currency={baseCcy}
                    zero={<span>{baseSymbol()}0</span>}
                  />
                )}
              </span>
            </span>
            <span className="commission-totals__sep">|</span>
            <span className="commission-totals__pair">
              <span className="strong-label">Total Commissions (Cost):</span>
              <span className="value">
                {fxHidden ? (
                  <FxUnavailable unavailable={fxUnavailable || gbpToBaseUnavailable} />
                ) : (
                  <Money amount={commissionsAmount * toBase} currency={baseCcy} zero={<span>{baseSymbol()}0</span>} />
                )}
                {!fxHidden &&
                  !!commissionsAmount &&
                  netProfitBeforeCommissions > 0 && (
                    <span className="value-pct">
                      /{" "}
                      {(
                        (commissionsAmount / netProfitBeforeCommissions) *
                        100
                      ).toFixed(2)}
                      %
                    </span>
                  )}
              </span>
            </span>
          </div>

          <div className="commission-totals__line">
            <span className="commission-totals__pair">
              <span className="strong-label">
                Net Profit After Commissions:
              </span>
              <span className="value">
                {fxHidden ? (
                  <FxUnavailable unavailable={fxUnavailable || gbpToBaseUnavailable} />
                ) : (
                  <Money
                    amount={netProfitAfterCommissions * toBase}
                    currency={baseCcy}
                    zero={<span>{baseSymbol()}0</span>}
                  />
                )}
              </span>
            </span>
            <Button
              variant="primary"
              className="commission-totals__save"
              data-btn="commission-save"
              onClick={saveCommissions}
            >
              Save & proceed
            </Button>
          </div>
        </div>
      </div>
    </Collapsible>
  );
}
